import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { SubscriptionsDataService, SubscriptionsColumnsService } from '../../services';
import { DialogSubscriptionFormComponent } from './subscription-form/subscription-form.component';
import { DialogSubscriptionDetailsComponent } from './subscription-details/subscription-details.component';
import { Subscription, SubscriptionsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class SubscriptionsComponent implements OnInit, OnDestroy {

  private subscriptionAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private subscriptionEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private subscriptionDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public subscriptionToDelete: Subscription = {};
  public subscriptions: Subscription[] = [];
  public subscription: Subscription = {};
  public selectedSubscriptions: Subscription[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private subscriptionsDataService: SubscriptionsDataService,
    private subscriptionsColumnsService: SubscriptionsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('SubscriptionsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Subscriptions - Management');
    this.primengConfig.ripple = true;
    this.getSubscriptions();
  }

  ngOnDestroy(): void {
    console.log('SubscriptionsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getSubscriptions(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.subscriptionsDataService.getSubscriptions(this.parameters).subscribe((response: SubscriptionsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.subscriptions = response.results;
        } else {
          this.subscriptions = this.subscriptions.concat(response.results);
        }
        if (response.inlineCount > this.subscriptions.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getSubscriptions();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SUBSCRIPTIONSNOTLOADED')});
    });
  }

  importSubscriptions(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.subscriptionsDataService.addSubscriptions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('SUBSCRIPTIONSCREATED')});
                if (proceed === j) {
                  this.getSubscriptions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SUBSCRIPTIONSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SUBSCRIPTIONSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.subscriptionsDataService.updateSubscriptions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('SUBSCRIPTIONSUPDATED')});
                if (proceed === j) {
                  this.getSubscriptions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SUBSCRIPTIONSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SUBSCRIPTIONSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.subscriptionsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('subscriptions.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedSubscriptions() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Subscriptions?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.subscriptionsDataService.deleteSubscriptions(this.selectedSubscriptions).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Subscriptions Deleted', life: 3000});
            this.getSubscriptions();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Subscriptions not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Subscriptions not deleted'});
        });
        this.selectedSubscriptions = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.subscriptions, this.columns, 'Subscriptions');
  }

  exportExcel() {
    ToolsService.exportExcel(this.subscriptions, this.columns, 'Subscriptions');
  }

  exportCsv() {
    ToolsService.exportCsv(this.subscriptions, this.columns, 'Subscriptions');
  }

  refresh() {
    this.subscriptionsDataService.clearCache();
    this.getSubscriptions();
  }

  subscriptionAdd(): void {
    if (!this.navigation.getParamValue('subscriptions.addInDialog', true)) {
      this.router.navigate(['/subscriptions/add']);
      return;
    }
    this.subscriptionAddDialog = this.dialogService.open(DialogSubscriptionFormComponent, {
      header: '', // this.translate.instant('NEWSUBSCRIPTION'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        subscription: {id: null},
        visits: this.subscriptionsColumnsService.visits,
        packs: this.subscriptionsColumnsService.packs,
        prices: this.subscriptionsColumnsService.prices,
        users: this.subscriptionsColumnsService.users,
      },
    });

    this.subscriptionAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Subscription added'});
        this.getSubscriptions();
      }
    });
  }

  subscriptionEdit(subscription: Subscription): void {
    if (!this.navigation.getParamValue('subscriptions.editInDialog', true)) {
      this.router.navigate(['/subscriptions/edit/' + subscription.id]);
      return;
    }
    this.subscriptionEditDialog = this.dialogService.open(DialogSubscriptionFormComponent, {
      header: '', // this.translate.instant('EDITSUBSCRIPTION') + ' ' + subscription.reference,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        subscription: subscription,
        visits: this.subscriptionsColumnsService.visits,
        packs: this.subscriptionsColumnsService.packs,
        prices: this.subscriptionsColumnsService.prices,
        users: this.subscriptionsColumnsService.users,
      },
    });

    this.subscriptionEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Subscription updated'});
        this.getSubscriptions();
      }
    });
  }

  subscriptionDetails(subscription: Subscription): void {
    if (!this.navigation.getParamValue('subscriptions.detailsInDialog', true)) {
      this.router.navigate(['/subscriptions/details/' + subscription.id]);
      return;
    }
    this.subscriptionDetailsDialog = this.dialogService.open(DialogSubscriptionDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        subscription: subscription,
      }
    });
  }

  subscriptionDelete(subscription: Subscription): void {
    this.subscriptionToDelete = subscription;
    this.messageService.clear();
    this.messageService.add({
      key: 'subscriptionDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + subscription.reference + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.subscriptionsDataService.deleteSubscription(this.subscriptionToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Subscription deleted'});
      this.getSubscriptions();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Subscription not deleted'});
    });
  }

  onDeleteReject(): void {
    this.subscriptionToDelete = {};
    this.messageService.clear('subscriptionDelete');
  }

}
